import { reactive, ref, unref, nextTick} from 'vue';
import { validataNull,deepClone } from '../utils';
/**
 * 通用表单 hook
 * @param {Object} initialOption 初始表单配置
 * @param {Object} initialData 初始表单数据
 * @returns {Object} 返回表单的状态和方法
 */

export function useForm(initialOption = {}, initialData = {}, initialRules = []) {
  // 表单数据和配置的响应式状态
  const state = reactive({
    formData: initialData,
    option: initialOption,
    rules:initialRules
  });

  // 控制表单更新的 key
  const key = ref(0);
  
  // 字段配置缓存
  let fieldsCache = null;
  let cacheVersion = 0;
  
  // 字典映射缓存
  const dicMapCache = new Map();

  // 初始化表单配置和数据
  function init(opt = {}, form = {}) {
    state.formData={}
    state.option={}
    if (opt && Object.keys(opt).length > 0) {
      state.option = opt;
    }
    if (form && Object.keys(form).length > 0) {
      state.formData = form;
    }
    // 清除缓存
    clearFieldsCache();
    key.value++;
  }
  function setColumn(field, prop, val) {
    const { found, config } = getFieldConfig(field,unref(state.option));
    if (found) {
      Reflect.set(config, prop, val);
      if(prop==='display'&&val===false){
        delete state.formData[field]
      }
    } else {
    }
  }
  function getColumn(field) {
    const { found, config } = getFieldConfig(field,unref(state.option));
    if (found) {
      return config;
    } else {
      return undefined
    }
  }
    function getFieldConfig(field, option) {
    // 检查参数有效性
    if (!option || !field) {
      return {
        found: false,
        config: null,
      };
    }

    // 查找 `columns` 中的配置
    if (option.columns && Array.isArray(option.columns)) {
      const columnConfig = option.columns.find((col) => col.field === field);
      if (columnConfig) {
        return {
          found: true,
          config: columnConfig,
        };
      }
    }

    // 查找 `groups` 中的配置
    if (option.groups && Array.isArray(option.groups)) {
      for (const group of option.groups) {
        if (group.columns && Array.isArray(group.columns)) {
          const groupColumnConfig = group.columns.find((col) => col.field === field);
          if (groupColumnConfig) {
            return {
              found: true,
              config: groupColumnConfig,
              group: group.label, // 可选：返回所属分组的标签
            };
          }
        }
      }
    }

    return {
      found: false,
      config: null,
    };
  }
  // 设置表单数据
  function set(field, value) {
    if(!validataNull(field)){
      state.formData[field] = value;
    }
    const item=getColumn(field)
    if(item&&item.onChange){
      item.onChange(value,events)
    }
  }

  function get(field) {
    if(validataNull(field)){
      return deepClone(unref(state.formData))
    }
    return unref(state.formData)[field];
  }

  // 异步获取表单数据，确保获取到最新值
  async function getAsync(field) {
    // 使用双重 nextTick 确保所有响应式更新完成
    // 现在 item-dimension 会立即触发 formData 更新，不需要等待防抖
    await nextTick();
    await nextTick();
    
    if(validataNull(field)){
      return deepClone(unref(state.formData))
    }
    return unref(state.formData)[field];
  }
   function display(field,val){
    if(typeof val === 'boolean'){
    const defaultVal=getColumn(field)?.default
      if(val){
        setColumn(field,"display",val)
        if(defaultVal!==undefined){
          set(field,defaultVal)
        }
      }
      else if(!val){
        setColumn(field,"display",val)
        delete state.formData[field]
      }
    }
  }
  /**
   * 获取表单数据的标签值映射
   * 将表单字段的值转换为对应的显示标签
   * @param {Array|string} fields 可选，指定要获取的字段，不传则获取所有字段
   * @returns {Object} 包含字段标签值的对象
   */
  function getLabelValue(fields = null) {
    const result = {};
    const formDataValue = unref(state.formData);
    
    if (fields) {
      const targetFields = Array.isArray(fields) ? fields : [fields];
      targetFields.forEach(fieldName => {
        const fieldConfig = getColumn(fieldName);
        if (fieldConfig && fieldConfig.display !== false) {
          const fieldValue = formDataValue[fieldName];
          result[fieldName] = convertValueToLabel(fieldValue, fieldConfig.dicData);
        }
      });
      return result;
    }
    
    const allFields = getAllFormFields();
    
    allFields.forEach(item => {
      if (item.display === false) return;
      const fieldValue = formDataValue[item.field];
      result[item.field] = convertValueToLabel(fieldValue, item.dicData);
    });
    
    return result;
  }

  /**
   * 清除字段配置缓存
   */
  function clearFieldsCache() {
    fieldsCache = null;
    dicMapCache.clear();
    cacheVersion++;
  }

  /**
   * 获取所有表单字段配置（带缓存）
   * @returns {Array} 所有字段配置的数组
   */
  function getAllFormFields() {
    if (fieldsCache && cacheVersion === key.value) {
      return fieldsCache;
    }
    
    const fields = [];
    
    if (state.option.columns?.length) {
      fields.push(...state.option.columns);
    }
    
    if (state.option.groups?.length) {
      state.option.groups.forEach(group => {
        if (group.columns?.length) {
          fields.push(...group.columns);
        }
      });
    }
    
    fieldsCache = fields;
    cacheVersion = key.value;
    
    return fields;
  }

  /**
   * 将字段值转换为标签
   * @param {*} fieldValue 字段值
   * @param {Array} dicData 字典数据
   * @returns {*} 转换后的标签值
   */
  function convertValueToLabel(fieldValue, dicData) {
    if (!dicData?.length) {
      return fieldValue;
    }
    
    if (fieldValue === null || fieldValue === undefined || fieldValue === '') {
      return fieldValue;
    }
    
    const dicMap = createDicMap(dicData);
    
    if (Array.isArray(fieldValue)) {
      return fieldValue.map(value => dicMap.get(value) ?? value).join(',');
    }
    return dicMap.get(fieldValue) ?? fieldValue;
  }

  /**
   * 创建字典数据的映射表（带缓存）
   * @param {Array} dicData 字典数据数组
   * @returns {Map} 值到标签的映射表
   */
  function createDicMap(dicData) {
    if (!dicData?.length) return new Map();
    
    const cacheKey = JSON.stringify(dicData);
    
    if (dicMapCache.has(cacheKey)) {
      return dicMapCache.get(cacheKey);
    }
    
    const map = new Map();
    dicData.forEach(item => {
      if (item && typeof item === 'object' && 'value' in item) {
        map.set(item.value, item.label ?? item.value);
      }
    });
    
    dicMapCache.set(cacheKey, map);
    
    return map;
  }
  const events={
    set,
    get,
    getAsync,
    init,
    setColumn,
    getColumn,
    display,
    getLabelValue
  }
  return {
    state,
    ...events,
    events,
    key,
  };
}
